import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountingResult, EntityViewCard, Invoice, InvoiceType, MetadataEntityData, MetadataEntityFieldData, MetadataEntityFieldDomain, MetadataEntityFieldType, PriorityUtility, WorkOrderStatusUtility } from '@jms/model';
import { AbsCurrencyFormatPipe } from '@jms/utils/util.pipes';
import { EntityManifestService } from '@workbuddy/services/entities';
import type { EntityManifestTagStyle } from '@workbuddy/services/entities';
import { CardComponent, CardDescriptionDirective, CardFooterDirective, CardIdentifierDirective, CardRowDirective, CardStatusDirective, CardTitleDirective } from '@workbuddy/ui/v2/components/card';
import { WbValueComponent } from '@workbuddy/ui/v2/components/primitives/value';
import { formatSiteOrAddress } from '@workbuddy/ui/v2/components/primitives/value/renderers/address';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { resolveValueRendererKind } from '@workbuddy/ui/v2/components/primitives/value/wb.value.renderer.kind';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';
import { TooltipModule } from 'primeng/tooltip';
import { ClippedTooltipDirective } from './clipped.tooltip.directive';
import { EntityBoardStore } from '../../state';

type CardFieldEntry = {
  field: MetadataEntityFieldData;
  value: any;
  /** Full, unclipped text for the row's native title — the ellipsised cell only shows a prefix. */
  title: string;
};

type FooterTag = {
  _id: string;
  name: string;
  color: string | null;
  backgroundColor?: string | null;
  borderColor?: string | null;
  mode?: BadgeMode;
  tooltip?: string | null;
};

// The financial system views include prepaymentTotal in the card body purely so the view-governed
// projection delivers it for the hero amount on prepayment documents. The body row itself is only
// meaningful on prepayment documents, so entity-card hides it for every other financial type.
const PREPAYMENT_TOTAL_FIELD: keyof Invoice = 'prepaymentTotal';

/** Card Amount Due for a prepayment. Hero amount stays prepaymentTotal. */
export function prepaymentAmountDue(item: { prepaymentTotal?: number | null; amountPaid?: number | null } | null | undefined): number {

  const total = Number(item?.prepaymentTotal ?? 0);
  const paid = Number(item?.amountPaid ?? 0);
  const due = Math.round((total - paid) * 100) / 100;
  return due > 0 ? due : 0;
}
const TYPE_FIELD: keyof Invoice = 'type';
const STATUS_FIELD: keyof Invoice = 'status';

@Component({
  selector: 'entity-card',
  templateUrl: './entity.card.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TooltipModule, ClippedTooltipDirective, CardComponent, CardIdentifierDirective, CardTitleDirective, CardDescriptionDirective, CardStatusDirective, CardRowDirective, CardFooterDirective, WbValueComponent, BadgeComponent],
  providers: [DateTimeFormatPipe],
})
export class EntityCardComponent {
  protected readonly BadgeMode = BadgeMode;
  private readonly dateFormatter = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  private readonly currencyFormatter = new AbsCurrencyFormatPipe();
  private readonly entityManifests = inject(EntityManifestService);
  private readonly boardStore = inject(EntityBoardStore, { optional: true });
  private readonly dateTimePipe = inject(DateTimeFormatPipe);
  private readonly generalSettings = inject(GeneralSettingService, { optional: true });
  /**
   * Region is read optionally: a card mounts wherever a collection renders, and the settings service
   * is not loaded in every one of those hosts. A missing region is a formatting default, never a
   * reason for the card to throw and take the whole board's render with it.
   */
  private readonly region = this.generalSettings?.generalSettings?.region;
  private readonly tenantTimezone = this.region?.timezone ?? 'UTC';
  private readonly tenantDateFormat = this.region?.dateFormat ?? 'dd/MM/yyyy';
  private readonly tenantTimeFormat = this.region?.timeFormat ?? 'HH:mm';

  public readonly item = input<any>(null);
  public readonly metadata = input<MetadataEntityData | null>(null);
  public readonly card = input<Partial<EntityViewCard> | null>(null);
  public readonly clickable = input(true);
  public readonly selected = input(false);
  public readonly compact = input(false);

  // Financial-document branch. Every finance-specific behaviour (hero amount, status-in-footer move,
  // type/integration badges) is gated behind this signal, so non-financial cards are behaviourally and
  // visually unchanged.
  protected readonly $isFinancial = computed(() => this.metadata()?.isFinancialDocument === true);

  protected readonly $fieldLookup = computed(() => this.buildFieldLookup(this.metadata()));
  protected readonly $identifierField = computed(() => this.resolveField(this.card()?.identifier));
  protected readonly $titleField = computed(() => this.resolveField(this.card()?.title));
  protected readonly $descriptionField = computed(() => this.resolveField(this.card()?.description));
  protected readonly $statusField = computed(() => this.resolveField(this.card()?.status));
  protected readonly $bodyFields = computed(() => this.resolveCardFields(this.card()?.body ?? []));
  protected readonly $footerFields = computed(() => this.resolveCardFields(this.card()?.footer ?? []));
  protected readonly $identifier = computed(() => this.getFieldDisplayValue(this.item(), this.$identifierField()));
  // Financial titles point at the embedded contact; prefer the customer/account name over the generic
  // object stringify (which favours the contact's address). Non-financial titles are unchanged.
  protected readonly $title = computed(() => {

    const generic = this.getFieldDisplayValue(this.item(), this.$titleField());
    if (!this.$isFinancial()) return generic;

    const field = this.$titleField();
    const value = field ? this.getValueFromPath(this.item(), field.path ?? field.name) : null;
    // A draft can save the contact with only its trading name; without it the card falls to the
    // address and titles the invoice with the site. Same rule as the table column (WBS-11813, WBS-12383).
    const name = typeof value === 'object' ? value?.name || value?.friendlyName : null;
    return typeof name === 'string' && name ? name : generic;
  });
  protected readonly $description = computed(() => this.getFieldDisplayValue(this.item(), this.$descriptionField()));
  protected readonly $descriptionValue = computed(() => {

    const field = this.$descriptionField();
    return field ? this.getValueFromPath(this.item(), field.path ?? field.name) : null;
  });

  // The card never sniffs the description text. The slot resolves to a field, wb-value picks the
  // renderer from its type, and the Address renderer owns what layout="lines" means — so an address
  // wraps into postal lines here while a text description still clamps, with no branch in this class.
  protected readonly $descriptionIsAddress = computed(() => {

    const field = this.$descriptionField();
    return !!field && resolveValueRendererKind(field, this.$descriptionValue()) === 'address';
  });
  // Non-financial hero (status slot) renders the configured status field value, unchanged. Financial
  // documents repurpose this slot for the hero amount, so $status stays blank for them and the amount is
  // rendered via $heroAmount instead.
  protected readonly $status = computed(() => (this.$isFinancial() ? '' : this.getFieldDisplayValue(this.item(), this.$statusField())));

  // Hero amount for financial documents only. The card's persisted `status` slot is repurposed to hold
  // the config-designated Currency field (relabelled "Amount" in the view-settings UI). Type resolution
  // is applied on top: prepayment types report prepaymentTotal; everything else reads that configured
  // field (Finance Pro system views project subTotal). Absolute currency so negatives never show.
  protected readonly $heroAmount = computed<string | null>(() => {

    if (!this.$isFinancial()) return null;

    const amountField = this.$statusField();
    if (!amountField) return null;

    const item = this.item();
    const rawAmount = this.resolveFinancialAmount(item, amountField);
    if (rawAmount == null) return null;

    return this.currencyFormatter.transform(rawAmount);
  });

  protected readonly $bodyEntries = computed<CardFieldEntry[]>(() => {

    const item = this.item();
    const fields = this.$bodyFields();
    return fields
      .filter(field => this.isBodyFieldRelevant(item, field))
      .map(field => {

        const path = field.path ?? field.name;
        // Stored outstanding is 0 until a prepayment is fully paid, then it goes negative for remaining credit.
        // The card's Amount Due is what is still to collect: prepayment total minus amount paid, never below 0.
        const value = path === 'outstandingAmount' && this.isPrepaymentType(item)
          ? prepaymentAmountDue(item)
          : this.getValueFromPath(item, path) ?? null;
        return { field, value, title: this.bodyRowTitle(field, value) };
      });
  });

  // The row already renders the whole date-time; the title repeats it in the tenant's configured
  // format so a value that wrapped or clipped is still readable in full on hover.
  private bodyRowTitle(field: MetadataEntityFieldData, value: any): string {

    const type = Array.isArray(field.type) ? field.type[0] : field.type;
    if (type !== MetadataEntityFieldType.DateTime || value == null) return this.stringifyValue(value);

    return this.dateTimePipe.transform(value, this.tenantTimezone, `${this.tenantDateFormat} ${this.tenantTimeFormat}`, '') || this.stringifyValue(value);
  }

  protected readonly $footerTags = computed<FooterTag[]>(() => {

    const item = this.item();
    const fields = this.$footerFields();
    const tags: FooterTag[] = [];
    const seen = new Set<string>();

    const neutralColor = 'var(--wb-color-foreground-tertiary)';

    const push = (tag: FooterTag) => {

      if (seen.has(tag._id)) return;
      seen.add(tag._id);
      tags.push(tag);
    };

    for (const field of fields) {
      const path = field.path ?? field.name;

      // Financial docs badge `type` via buildFinancialTypeTag (CREDIT NOTE / PP only — standard
      // documents get no type badge), so the raw enum value is skipped here.
      if (this.$isFinancial() && path === TYPE_FIELD) continue;

      const value = this.getValueFromPath(item, path);
      if (value == null) continue;

      const values = Array.isArray(value) ? value : [value];
      for (const entry of values) {
        if (typeof entry === 'object' && entry?._id) {
          push({ _id: entry._id, name: entry.name ?? '', color: entry.color ?? PriorityUtility.getColor(entry) ?? WorkOrderStatusUtility.getColor(entry) ?? neutralColor });
          continue;
        }

        if (typeof entry === 'string' || typeof entry === 'number') {
          if (this.$isFinancial() && path === STATUS_FIELD) {
            if (this.matchesSegmentStatus(entry)) continue;
            const style = this.resolveTagStyle(path, entry, item);
            if (style) {
              // The manifest tag may rename a status (a template's Approved reads Public); the header uses the same label.
              const label = (style as EntityManifestTagStyle & { readonly label?: string }).label ?? String(entry);
              push({ _id: String(entry), name: label, color: style.color, backgroundColor: style.backgroundColor, borderColor: style.borderColor, mode: BadgeMode.Squared });
              continue;
            }
          }

          push({ _id: String(entry), name: String(entry), color: neutralColor });
        }
      }
    }

    // Financial documents additionally surface a type badge (CREDIT NOTE / PP) and an accounting-sync
    // badge derived from the embedded AccountingResult. Both are appended only in the financial branch,
    // so non-financial cards keep byte-identical footer output.
    if (this.$isFinancial()) {
      const typeTag = this.buildFinancialTypeTag(item);
      if (typeTag) push(typeTag);

      const integrationTag = this.buildIntegrationTag(item);
      if (integrationTag) push(integrationTag);
    }

    return tags;
  });

  // Prepayment document types report prepaymentTotal; everything else (including credit notes) reports
  // the view-configured amount field (subTotal on Finance Pro system views).
  private resolveFinancialAmount(item: any, amountField: MetadataEntityFieldData): number | null {

    if (!item) return null;

    const value = this.isPrepaymentType(item) ? item[PREPAYMENT_TOTAL_FIELD] : this.getValueFromPath(item, amountField.path ?? amountField.name);
    if (typeof value === 'number') return value;

    // An unpriced financial document reads as 0.00, not as a blank slot: stating a price is the
    // whole point of these documents, and drafts commonly persist null totals before pricing.
    // Only a genuinely non-numeric value (a string that will not coerce) falls back to null.
    if (value == null) return 0;

    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  }

  private isPrepaymentType(item: any): boolean {

    const type = item?.type as InvoiceType | undefined;
    return type === InvoiceType.PrepaymentInvoice || type === InvoiceType.PrepaymentSupplier;
  }

  // Financial branch only (non-financial cards are unaffected):
  // - a field already occupying a header slot (hero amount/identifier/title/description) never
  //   repeats as a body row — e.g. a saved view with outstandingAmount as both hero and body row;
  // - a currency body row whose number equals the hero (Draft Amount Due === Total) is dropped;
  // - the prepaymentTotal body row is a projection carrier for the hero amount and renders only on
  //   prepayment documents (never as a $0.00 row elsewhere).
  private isBodyFieldRelevant(item: any, field: MetadataEntityFieldData): boolean {

    if (!this.$isFinancial()) return true;

    const path = field.path ?? field.name;
    const card = this.card();
    if ([card?.status, card?.identifier, card?.title, card?.description].includes(path)) return false;

    if (path === PREPAYMENT_TOTAL_FIELD) return this.isPrepaymentType(item);

    if (field.type === MetadataEntityFieldType.Currency) {
      const heroField = this.$statusField();
      if (heroField) {
        const hero = this.resolveFinancialAmount(item, heroField);
        const raw = this.getValueFromPath(item, path);
        const amount = typeof raw === 'number' ? raw : Number(raw);
        if (hero != null && !Number.isNaN(amount) && amount === hero) return false;
      }
    }

    return true;
  }

  /** True when the active board segment already filters this exact status (Draft tile → hide Draft badge). */
  private matchesSegmentStatus(status: string | number): boolean {

    const store = this.boardStore;
    if (!store) return false;
    const segment = store.$segmentFilters().find(item => item._id === store.$segmentId());
    const statusFilters = (segment?.filters ?? []).filter(filter => filter._id === STATUS_FIELD);
    if (statusFilters.length !== 1) return false;
    return String(statusFilters[0].value) === String(status);
  }

  private buildFinancialTypeTag(item: any): FooterTag | null {

    const type = item?.type as InvoiceType | undefined;
    if (!type) return null;

    const style = this.resolveTagStyle(TYPE_FIELD, type, item);
    if (!style) return null;

    const label = this.isPrepaymentType(item) ? 'PP' : 'CREDIT NOTE';

    return { _id: `type:${type}`, name: label, color: style.color, backgroundColor: style.backgroundColor, borderColor: style.borderColor, mode: BadgeMode.Squared };
  }

  // Accounting-sync badge. The manifest tag owns both colour AND copy (Synced / Accounting
  // error). Printing `integration.type` here dumped "MYOB Acumatica" as a status chip.
  private buildIntegrationTag(item: any): FooterTag | null {

    const integration = item?.integration as AccountingResult | undefined;
    const style = this.resolveTagStyle('integration', integration, item) as (EntityManifestTagStyle & { readonly label?: string; readonly tooltip?: string }) | null;
    const label = style?.label;
    if (!label) return null;

    return { _id: `integration:${integration?.type ?? label}`, name: label, color: style.color, backgroundColor: style.backgroundColor, borderColor: style.borderColor, mode: BadgeMode.Squared, tooltip: style.tooltip ?? (integration?.success === false ? integration.error ?? null : null) };
  }

  private resolveTagStyle(field: string, value: unknown, item: unknown): EntityManifestTagStyle | null {

    const entity = this.metadata()?.type;
    return entity ? this.entityManifests.tagStyle(entity, field, value, item) : null;
  }

  private resolveCardFields(configuredPaths: string[]): MetadataEntityFieldData[] {

    return configuredPaths
      .map(path => this.resolveField(path) ?? this.createFieldStub(path))
      .filter((field, index, fields) => !!field && fields.findIndex(current => current.path === field.path) === index);
  }

  private resolveField(path: string | null | undefined): MetadataEntityFieldData | null {
    if (!path) {
      return null;
    }

    return this.$fieldLookup().get(path) ?? null;
  }

  private getFieldFromPath(fields: MetadataEntityFieldData[], path: string): MetadataEntityFieldData | null {

    const directField = fields.find(field => field.path === path || field.name === path);
    if (directField) return directField;

    const [parentPath, ...rest] = path.split('.');
    if (!parentPath || !rest.length) return null;

    const parentField = fields.find(field => field.path === parentPath || field.name === parentPath);
    const childFields = parentField?.typeData?.fields ?? [];
    if (!childFields.length) return null;

    const childField = this.getFieldFromPath(childFields, rest.join('.'));
    if (!childField) return null;

    // Preserve the full dot path so getValueFromPath resolves correctly
    return { ...childField, path };
  }

  private getFieldDisplayValue(item: any, field: MetadataEntityFieldData | null): string {
    if (!field) {
      return '';
    }

    return this.stringifyValue(this.getValueFromPath(item, field.path ?? field.name));
  }

  private getValueFromPath(item: any, path: string | null | undefined): any {
    if (!item || !path) {
      return null;
    }

    return path.split('.').reduce((acc, part) => acc?.[part], item);
  }

  private stringifyValue(value: any): string {
    if (value == null) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map(item => this.stringifyValue(item)).filter(Boolean).join(', ');
    }

    if (typeof value === 'string') {
      return this.isIsoDateString(value) ? this.dateFormatter.format(new Date(value)) : value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      // One formatter for site/address text so the card's title attribute and the rendered value
      // agree on both the street and on whether the site name repeats its own address. The old
      // local fallback read `value.street`, a key no Address carries, and silently lost the street.
      if (value.address || value.street1 != null || value.suburb != null || value.postalCode != null || value.postcode != null) {
        return formatSiteOrAddress(value);
      }

      const label = value.name ?? value.label ?? value.displayName ?? value.systemName ?? value.systemStatus ?? value.value;

      if (label != null && label !== '') {
        return String(label);
      }
      // A lookup with no name (legacy `{}` / `{ _id: null }`) shows nothing, like null, never "[object Object]" (WBS-11494).
      return '';
    }

    return String(value);
  }

  private createFieldStub(path: string): MetadataEntityFieldData {
    return { path, name: path, displayName: this.humanizePath(path) } as MetadataEntityFieldData;
  }

  private buildFieldLookup(metadata: MetadataEntityData | null): Map<string, MetadataEntityFieldData> {

    if (!metadata) {
      return new Map<string, MetadataEntityFieldData>();
    }

    const domain = new MetadataEntityFieldDomain(metadata);
    const lookup = new Map<string, MetadataEntityFieldData>();

    for (const field of Object.values(domain.uiFields)) {
      if (field.path) {
        lookup.set(field.path, field);
      }

      if (field.name && !lookup.has(field.name)) {
        lookup.set(field.name, field);
      }
    }

    return lookup;
  }

  private humanizePath(path: string): string {
    const parts = path.split('.');
    const lastPart = parts[parts.length - 1];
    const labelSource = ['name', 'systemName', 'displayName'].includes(lastPart) && parts.length > 1 ? parts[parts.length - 2] : lastPart;

    return labelSource
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, match => match.toUpperCase());
  }

  private isIsoDateString(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
  }
}
